import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { CalendarHeaderComponent } from './calendar-header.component';

@Component({
  selector: 'app-datetime-picker',
  standalone: true,
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true
    }
  ]
})
export class DateTimePickerComponent implements ControlValueAccessor {
  @Input() dateOnly: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() autoSelectOnDateClick: boolean = false;
  @Input() placeholder: string = 'Selecione uma data';
  @Output() dateChange = new EventEmitter<Date>();

  openPopup = false;
  selectedDate = new Date();
  hour = new Date().getHours() % 12 || 12;
  minute = 0;
  period: 'AM' | 'PM' = 'PM';
  disabled = false;
  selectedDateFormat = "";
  customHeader = CalendarHeaderComponent;

  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(private eRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (this.openPopup && !this.eRef.nativeElement.contains(event.target)) {
      this.openPopup = false;
      this.onTouched();
    }
  }

  writeValue(value: Date | null): void {
    if (value) {
      this.selectedDate = value;
      const h = value.getHours();
      this.hour = h % 12 || 12;
      this.period = h >= 12 ? 'PM' : 'AM';
      this.minute = value.getMinutes();
      this.selectedDateFormat = this.formattedDate();
    } else {
      this.selectedDateFormat = '';
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  formattedDate(): string {
    if (this.disabled) {
        return "";
    }
    const dd = this.two(this.selectedDate.getDate());
    const mm = this.two(this.selectedDate.getMonth() + 1);
    const yyyy = this.selectedDate.getFullYear();

    if (this.dateOnly) {
      return `${dd}/${mm}/${yyyy}`;
    }

    const hh = this.two(this.hour);
    const min = this.two(this.minute);
    return `${dd}/${mm}/${yyyy} ${hh}:${min} ${this.period}`;
  }

  adjustHour(delta: number) {
    this.hour = ((this.hour + delta - 1 + 12) % 12) + 1;
  }

  adjustMinute(delta: number) {
    const availableMinutes = [0, 15, 30, 45];
    let currentIndex = availableMinutes.indexOf(this.minute);

    if (currentIndex === -1) {
      const closest = availableMinutes.reduce((prev, curr) => {
        return (Math.abs(curr - this.minute) < Math.abs(prev - this.minute) ? curr : prev);
      });
      this.minute = closest;
      currentIndex = availableMinutes.indexOf(this.minute);
    }

    let newIndex = currentIndex + delta;

    if (newIndex < 0) {
      newIndex = availableMinutes.length - 1;
    } else if (newIndex >= availableMinutes.length) {
      newIndex = 0;
    }

    this.minute = availableMinutes[newIndex];
  }

  togglePeriod() {
    this.period = this.period === 'AM' ? 'PM' : 'AM';
  }

  onCalendarDateSelected(date: Date | null) {
    if (!date) {
      return;
    }

    this.selectedDate = date;

    if (this.autoSelectOnDateClick && this.dateOnly) {
      this.confirm();
    }
  }

  confirm() {
    // Verificar se a data está dentro dos limites
    if (this.minDate && this.selectedDate < this.minDate) {
      alert('A data não pode ser anterior à data de início!');
      return;
    }

    if (this.maxDate && this.selectedDate > this.maxDate) {
      alert('A data não pode ser posterior à data máxima permitida!');
      return;
    }

    const h24 = this.period === 'PM' ? (this.hour % 12 + 12) : (this.hour % 12);

    const finalDate = new Date(
      this.selectedDate.getFullYear(),
      this.selectedDate.getMonth(),
      this.selectedDate.getDate(),
      this.dateOnly ? 0 : h24,
      this.dateOnly ? 0 : this.minute,
      0,
      0
    );

    this.onChange(finalDate);
    this.dateChange.emit(finalDate);
    this.selectedDateFormat = this.formattedDate();
    this.openPopup = false;
    this.onTouched();
  }

  cancel() {
    this.openPopup = false;
    this.onTouched();
  }

  two(n: number): string {
    return n < 10 ? '0' + n : n.toString();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
